import type { ScopeComponent } from '@/types';

/**
 * How a scope component's data is obtained:
 * - `automated` — fulfilled by an external API capability.
 * - `manual` — fulfilled by a human (manual Backoffice task), or not yet wired.
 *
 * Derived from `hasApiDependency` (set by the backend from the component's
 * capabilities). Only meaningful for *data leaves* (components with no
 * dependencies); sections render from their dependencies rather than fetching
 * data themselves.
 */
export type Fulfillment = 'automated' | 'manual';

/** A data leaf has no dependencies — it fetches/produces data directly. */
export function isDataLeaf(sc: Pick<ScopeComponent, 'dependencies'>): boolean {
  return sc.dependencies.length === 0;
}

export function leafFulfillment(sc: Pick<ScopeComponent, 'hasApiDependency'>): Fulfillment {
  return sc.hasApiDependency ? 'automated' : 'manual';
}

/** Mantine Badge props for a fulfillment value — keeps colour/label consistent. */
export function fulfillmentBadge(f: Fulfillment): { label: string; color: string; title: string } {
  return f === 'automated'
    ? { label: 'Automated', color: 'blue', title: 'Fulfilled by an external API data source' }
    : { label: 'Manual', color: 'orange', title: 'Fulfilled by a manual (human) Backoffice task' };
}

/**
 * The manual data leaves a section (transitively) depends on, by slug.
 *
 * Walks `scope_component` dependencies down to their leaves and collects any
 * that are manual. `all_sections` aggregator deps are intentionally skipped —
 * otherwise summary sections (executive-summary, etc.) would always flag manual
 * whenever any unrelated section in the report is manual, which isn't useful.
 *
 * Returns an empty array for a section with no manual leaves (or for a leaf).
 */
export function manualLeafSlugs(
  sc: ScopeComponent,
  bySlug: Map<string, ScopeComponent>,
  seen = new Set<string>()
): string[] {
  const result = new Set<string>();
  for (const dep of sc.dependencies) {
    if (dep.type !== 'scope_component') {
      continue; // skip all_sections aggregators
    }
    const slug = dep.scope_component_slug;
    if (seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    const target = bySlug.get(slug);
    if (!target) {
      continue;
    }
    if (isDataLeaf(target)) {
      if (leafFulfillment(target) === 'manual') {
        result.add(slug);
      }
    } else {
      for (const nested of manualLeafSlugs(target, bySlug, seen)) {
        result.add(nested);
      }
    }
  }
  return [...result];
}

/**
 * The API-fulfilled data leaves a section (transitively) depends on.
 *
 * Same walk as `manualLeafSlugs` — including skipping `all_sections`
 * aggregators — but returns the leaves themselves, since callers need each
 * leaf's declared `providers`. Leaves with no providers (human-fulfilled, or
 * fulfilled by research infrastructure rather than a data provider) are
 * omitted: there is nothing to choose between.
 *
 * Deduped by slug, so a leaf two sibling sections both depend on appears once.
 */
export function apiLeaves(
  sc: ScopeComponent,
  bySlug: Map<string, ScopeComponent>,
  seen = new Set<string>()
): ScopeComponent[] {
  const result = new Map<string, ScopeComponent>();
  for (const dep of sc.dependencies) {
    if (dep.type !== 'scope_component') {
      continue;
    }
    const slug = dep.scope_component_slug;
    if (seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    const target = bySlug.get(slug);
    if (!target) {
      continue;
    }
    if (isDataLeaf(target)) {
      if ((target.providers?.length ?? 0) > 0) {
        result.set(slug, target);
      }
    } else {
      for (const nested of apiLeaves(target, bySlug, seen)) {
        result.set(nested.slug, nested);
      }
    }
  }
  return [...result.values()];
}
